import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import CloseIcon from "@assets/icons/close.svg";
import SearchIcon from "@assets/icons/search.svg";

import { useEscapeKey } from "@hooks/useEscapeKey";
import { onFocusWithoutScroll } from "@utils/misc.util";
import { cn } from "@utils/tailwind.util";
import { User, UserForDropdown } from "@app-types/common.type";
import { IPeople } from "@interfaces/people.interface";
import { AttendeeOptions } from "./AttendeeOptions";

interface IAttendeesDropdownProps {
  options: UserForDropdown[];
  attendees: User[];
  people: IPeople[];
  isAllMembersSelected: boolean;
  onDeleteAttendee?: (id: string) => void;
  onAddAttendee?: (user: UserForDropdown) => void;
  isDropdownOpen: boolean;
  onCloseDropdown: () => void;
  dropdownClassNames?: string;
  searchValueAttendeesDropdown: string;
  setSearchValueAttendeesDropdown: (value: string) => void;
  enableCompaniesAndPeople?: boolean;
}

export function AttendeesDropdown({
  options,
  attendees,
  people,
  isAllMembersSelected,
  onDeleteAttendee,
  onAddAttendee,
  isDropdownOpen,
  onCloseDropdown,
  dropdownClassNames = "",
  searchValueAttendeesDropdown,
  setSearchValueAttendeesDropdown,
  enableCompaniesAndPeople = false,
}: IAttendeesDropdownProps) {
  const [portalRoot, setPortalRoot] = useState<HTMLElement | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const showDropdown = isDropdownOpen && !!dropdownPosition && portalRoot;
  const selectedOptions = [...attendees, ...people];

  const handleSelect = (user: UserForDropdown) => {
    const selectedOptionIds = selectedOptions.map((option) => option.id);
    const isSelected = selectedOptionIds.includes(user.id);

    if (isSelected) {
      onDeleteAttendee(user.id);
    } else if (!user.external) {
      onAddAttendee(user);
    } else {
      onAddAttendee(user);
      onCloseDropdown();
      setSearchValueAttendeesDropdown("");
    }
  };

  useEscapeKey({ callback: () => onCloseDropdown(), enabled: isDropdownOpen });

  useEffect(() => {
    const handleClickOutside = (event: PointerEvent) => {
      const target = event.target as Node;

      if (dropdownRef.current?.contains(target) || inputRef.current?.contains(target)) {
        return;
      }

      if (isDropdownOpen && dropdownRef.current && !dropdownRef.current.contains(target)) {
        onCloseDropdown();
      }
    };

    if (isDropdownOpen) {
      document.addEventListener("pointerdown", handleClickOutside);
    }
    return () => document.removeEventListener("pointerdown", handleClickOutside);
  }, [isDropdownOpen]);

  useEffect(() => {
    if (isDropdownOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.top + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    } else {
      setDropdownPosition(null);
    }
  }, [isDropdownOpen]);

  useEffect(() => {
    if (showDropdown) {
      inputRef.current?.focus({ preventScroll: true });
    }
  }, [showDropdown]);

  useEffect(() => {
    const el = document.getElementById("portal-root");
    setPortalRoot(el);
  }, []);

  const { top, left, width } = dropdownPosition || {};

  return (
    <div ref={containerRef} className="flex flex-col gap-2 p-0">
      {showDropdown &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{ top, left, width }}
            className="absolute z-[50] flex items-center gap-[6px] h-9 p-2 pr-3 bg-white dark:bg-gray-850 border dark:border-gray-600 box-border rounded-lg focus-within:shadow-mainButtonFocus focus:border focus:border-stroke-actionPrimary"
            onMouseDown={(e) => e.preventDefault()} // Stop native scrolling
          >
            <SearchIcon width={20} height={20} className="text-content-secondary dark:text-white" />

            <input
              ref={inputRef}
              value={searchValueAttendeesDropdown}
              autoComplete="off"
              aria-owns="options"
              aria-controls="options"
              aria-autocomplete="list"
              aria-expanded="true"
              autoCapitalize="off"
              role="combobox"
              className="peer bg-white dark:bg-gray-850 dark:text-white w-full appearance-none border-none pr-6 py-0 text-sm text-content-primary outline-none placeholder:text-content-disabled focus:ring-0"
              onChange={(ev) => setSearchValueAttendeesDropdown(ev.target.value)}
              placeholder="Search"
              onFocus={(event) => onFocusWithoutScroll(event, inputRef)}
            />

            {searchValueAttendeesDropdown && (
              <button
                type="button"
                className="absolute h-4 top-1 right-4 text-content-secondary hover:text-content-primary dark:hover:text-invertedPrimary80"
                onClick={() => setSearchValueAttendeesDropdown("")}
                onMouseDown={(e) => e.preventDefault()} // Stop native scrolling
                aria-label="Clear search"
              >
                <CloseIcon
                  width={16}
                  height={16}
                  className="inline-block align-middle dark:text-white"
                />
              </button>
            )}

            <div
              className={cn(
                "absolute left-0 min-h-[50px] max-h-[360px] w-full flex flex-col items-center justify-center top-[38px] z-50 text-content-primary",
                "divide-y divide-stroke-primary rounded-md border border-stroke-primary dark:border-gray-600 bg-white dark:bg-gray-850 shadow-navItem md:w-[450px]",
                dropdownClassNames
              )}
            >
              <AttendeeOptions
                options={options}
                selectedOptions={selectedOptions}
                isAllMembersSelected={isAllMembersSelected}
                onSelect={handleSelect}
                searchQuery={searchValueAttendeesDropdown}
                enableCompaniesAndPeople={enableCompaniesAndPeople}
              />
            </div>
          </div>,
          portalRoot
        )}
    </div>
  );
}
